import { useState, useEffect } from 'react';
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import Header from "./components/Header";
import Hero from "./components/Hero";
import Marquee from "./components/Marquee";
import Quote from "./components/Quote";
import About from "./components/About";
import Services from "./components/Services";
import Process from "./components/Process";
import CTABanner from "./components/CTABanner";
import FAQ from "./components/FAQ";
import ContactSection from "./components/ContactSection";
import Footer from "./components/Footer";
import BookingPage from "./components/BookingPage";
import ThankYou from "./components/ThankYou";
import ServicePage from "./pages/ServicePage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import TermsOfServicePage from "./pages/TermsOfServicePage";

const Home = () => {
  const [currentView, setCurrentView] = useState('landing'); // 'landing', 'booking', 'thankyou'
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Listen for Calendly booking confirmation
  useEffect(() => {
    const handleCalendlyEvent = (e) => {
      if (e.data.event && e.data.event.indexOf('calendly.event_scheduled') !== -1) {
        setCurrentView('thankyou');
        window.scrollTo(0, 0);
      }
    };

    window.addEventListener('message', handleCalendlyEvent);
    return () => window.removeEventListener('message', handleCalendlyEvent);
  }, []);

  const goToBooking = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentView('booking');
      window.scrollTo(0, 0);
      setIsTransitioning(false);
    }, 300);
  };

  const goToSection = (sectionId) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentView('landing');
      setTimeout(() => {
        if (sectionId && sectionId !== 'home') {
          const element = document.querySelector(sectionId);
          if (element) {
            element.scrollIntoView({ behavior: 'smooth' });
          }
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }
      }, 50);
      setIsTransitioning(false);
    }, 300);
  };

  const contentOpacity = isTransitioning ? 'opacity-0' : 'opacity-100';
  const transitionClass = 'transition-opacity duration-300';

  // Global scroll fade-in for all sections and footer
  useEffect(() => {
    if (currentView !== 'landing') return;
    let observer;
    const timer = setTimeout(() => {
      const targets = document.querySelectorAll('section, footer');
      targets.forEach(el => el.classList.add('scroll-fade'));
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(e => {
            if (e.isIntersecting) {
              e.target.classList.add('in-view');
              observer.unobserve(e.target);
            }
          });
        },
        { threshold: 0.07 }
      );
      targets.forEach(el => observer.observe(el));
    }, 60);
    return () => {
      clearTimeout(timer);
      if (observer) observer.disconnect();
    };
  }, [currentView]);

  if (currentView === 'thankyou') {
    return (
      <div className={`min-h-screen ${contentOpacity} ${transitionClass}`}>
        <style>{`
          @keyframes pageLoadFadeIn {
            from { opacity: 0; }
            to   { opacity: 1; }
          }
          .page-content {
            opacity: 0;
            animation: pageLoadFadeIn 0.7s ease-out forwards;
          }
        `}</style>
        <div className="page-content">
          <Header onBooking={goToBooking} onSectionClick={goToSection} />
          <ThankYou />
        </div>
      </div>
    );
  }

  if (currentView === 'booking') {
    return (
      <div className={`min-h-screen ${contentOpacity} ${transitionClass}`}>
        <style>{`
          @keyframes pageLoadFadeIn {
            from { opacity: 0; }
            to   { opacity: 1; }
          }
          .page-content {
            opacity: 0;
            animation: pageLoadFadeIn 0.7s ease-out forwards;
          }
        `}</style>
        <div className="page-content">
          <Header onBooking={goToBooking} onSectionClick={goToSection} />
          <BookingPage />
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${contentOpacity} ${transitionClass}`}>
      <style>{`
        @keyframes pageLoadFadeIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        .page-content {
          opacity: 0;
          animation: pageLoadFadeIn 0.7s ease-out forwards;
        }
      `}</style>
      <div className="page-content">
        <Header onBooking={goToBooking} onSectionClick={goToSection} />
        <Hero onBooking={goToBooking} />
        <Marquee />
        <About />
        <Services />
        <Process />
        <CTABanner onBooking={goToBooking} />
        <FAQ />
        <ContactSection onBooking={goToBooking} />
        <Footer onSectionClick={goToSection} />
      </div>
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/services/lead-generation" element={<ServicePage slug="lead-generation" />} />
          <Route path="/services/sales-funnels" element={<ServicePage slug="sales-funnels" />} />
          <Route path="/services/crm-automation" element={<ServicePage slug="crm-automation" />} />
          <Route path="/services/web-development" element={<ServicePage slug="web-development" />} />
          <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
          <Route path="/terms-of-service" element={<TermsOfServicePage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
